import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../../lib/api';
import toast from 'react-hot-toast';

export const ORGANIZATIONS_QUERY_KEY = 'super-admin-organizations';

function serverErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const first = data.errors[0];
    return first?.msg || first?.message || fallback;
  }
  return fallback;
}

/**
 * Paginated organisation list for the platform super-admin screen.
 * Keyed on page / search / status. Caller must gate with `enabled`.
 */
export function useOrganizationsList({ page, search, status, enabled }) {
  const { data, isLoading, error } = useQuery(
    [ORGANIZATIONS_QUERY_KEY, page, search, status],
    () =>
      api
        .get('/super-admin/organizations', {
          params: {
            page,
            limit: 50,
            search: search || undefined,
            status: status || undefined,
          },
        })
        .then((res) => res.data),
    { enabled: !!enabled, keepPreviousData: true }
  );

  return { data, isLoading, error };
}

/**
 * Create organisation + first admin. Invalidates the list on success.
 */
export function useCreateOrganization({ onSuccess } = {}) {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => api.post('/super-admin/organizations', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([ORGANIZATIONS_QUERY_KEY]);
        toast.success('Organisation created');
        onSuccess?.(res);
      },
      onError: (err) => {
        toast.error(serverErrorMessage(err, 'Failed to create organisation'));
      },
    }
  );
}

/**
 * Activate / suspend an organisation. Invalidates the list on success.
 */
export function useSetOrganizationStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, active }) => api.patch(`/super-admin/organizations/${id}/status`, { active }),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries([ORGANIZATIONS_QUERY_KEY]);
        toast.success(variables.active ? 'Organisation reactivated' : 'Organisation suspended');
      },
      onError: (err) => {
        toast.error(serverErrorMessage(err, 'Failed to update organisation status'));
      },
    }
  );
}
